import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import ConferenceAffiliationFormModal, {
  ConferenceAffiliationFormValues,
  formValuesToWriteBody,
} from '../components/conference/ConferenceAffiliationFormModal';
import ConferenceHistoryTable from '../components/conference/ConferenceHistoryTable';
import Spinner from '../components/Spinner';
import {
  createConferenceAffiliation,
  fetchConferenceHistory,
  isConferenceHistoryError,
  updateConferenceAffiliation,
} from '../services/conferenceHistoryService';
import { DashboardOutletContextValue } from '../types/dashboard';
import { ConferenceAffiliation, ConferenceHistoryResponse } from '../types/conferenceHistory';

type ModalMode = 'add' | 'edit' | null;

function ConferenceHistoryPage() {
  const { username, selectedSport } = useOutletContext<DashboardOutletContextValue>();

  const [data, setData] = useState<ConferenceHistoryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [editingRow, setEditingRow] = useState<ConferenceAffiliation | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    if (!username) {
      setError('Please sign in again to view conference history.');
      setData(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchConferenceHistory({ username, selectedSport });
      setData(response);
    } catch (err) {
      console.error('Conference history fetch failed:', err);
      setError('Unable to load conference history.');
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [username, selectedSport]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const closeModal = () => {
    setModalMode(null);
    setEditingRow(null);
    setFormError(null);
    setIsSubmitting(false);
  };

  const openAdd = () => {
    setEditingRow(null);
    setFormError(null);
    setModalMode('add');
  };

  const openEdit = (row: ConferenceAffiliation) => {
    setEditingRow(row);
    setFormError(null);
    setModalMode('edit');
  };

  const handleSubmit = async (values: ConferenceAffiliationFormValues) => {
    if (!username) {
      setFormError('Please sign in again.');
      return;
    }

    setIsSubmitting(true);
    setFormError(null);

    const request = { username, selectedSport };
    const body = formValuesToWriteBody(values);

    const result =
      modalMode === 'edit' && editingRow
        ? await updateConferenceAffiliation(request, editingRow.id, body)
        : await createConferenceAffiliation(request, body);

    setIsSubmitting(false);

    if (isConferenceHistoryError(result)) {
      setFormError(result.message);
      return;
    }

    if (result.propagation?.status === 'queued') {
      setSaveNotice(
        'Affiliation saved. Conference names are updating across your ingested data for the affected seasons.'
      );
      window.setTimeout(() => setSaveNotice(null), 5000);
    }

    closeModal();
    await loadHistory();
  };

  const affiliations = data?.affiliations ?? [];
  const teamLabel = data?.teamName?.trim() || 'your team';

  return (
    <section className="space-y-6">
      <header className="rounded-[22px] border border-slate-200 bg-white px-6 py-7 shadow-[0_24px_54px_-46px_rgba(15,23,42,0.65)]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-sky-500">
              {selectedSport}
            </p>
            <h1 className="mt-3 text-[42px] font-semibold tracking-tight text-[#16233d]">
              Conference History
            </h1>
            <p className="mt-3 max-w-2xl text-[18px] leading-8 text-[#65738d]">{teamLabel}</p>
          </div>
          <button
            type="button"
            onClick={openAdd}
            className="rounded-xl bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-sky-700"
          >
            Add affiliation
          </button>
        </div>
      </header>

      {saveNotice && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          {saveNotice}
        </div>
      )}

      {isLoading ? (
        <div className="flex min-h-[220px] items-center justify-center rounded-[22px] border border-slate-200 bg-white shadow-[0_20px_44px_-34px_rgba(15,23,42,0.55)]">
          <Spinner />
        </div>
      ) : error ? (
        <div className="rounded-[22px] border border-rose-200 bg-rose-50 px-6 py-8 text-rose-700">
          {error}
        </div>
      ) : (
        <ConferenceHistoryTable
          affiliations={affiliations}
          sportLabel={selectedSport}
          onEdit={openEdit}
        />
      )}

      {modalMode ? (
        <ConferenceAffiliationFormModal
          title={modalMode === 'add' ? 'Add conference affiliation' : 'Edit conference affiliation'}
          initial={editingRow}
          isSubmitting={isSubmitting}
          errorMessage={formError}
          onClose={closeModal}
          onSubmit={handleSubmit}
        />
      ) : null}
    </section>
  );
}

export default ConferenceHistoryPage;
